import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getPipelinesCost } from '../../../api/api';
import plainImage from '../../../assets/plain.ico';
import './side.css';

const Side = ({ isSidebarActive, artFormats, selectedFormat, onFormatSelect, userProfile }) => {
  const [pipelinesCost, setPipelinesCost] = useState({});
  const sidebarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside for desktop
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isMobile && 
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target) && 
          isSidebarActive) {
        onFormatSelect(selectedFormat);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarActive, onFormatSelect, selectedFormat, isMobile]);

  // Mobile touch handling
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe && isSidebarActive) {
      onFormatSelect(selectedFormat);
    } else if (isRightSwipe && !isSidebarActive) {
      onFormatSelect(selectedFormat);
    }
  }, [touchStart, touchEnd, isSidebarActive, onFormatSelect, selectedFormat]);

  // Fetch pipeline costs
  useEffect(() => {
    const fetchPipelinesCost = async () => {
      try {
        const response = await getPipelinesCost();
        setPipelinesCost(response.data.data);
      } catch (error) {
        console.error('Error fetching pipeline costs:', error);
      }
    };
    fetchPipelinesCost();
  }, []);

  // Loading state
  if (!artFormats) {
    return (
      <>
        <div 
          ref={sidebarRef}
          className={`inspira-sidebar ${isSidebarActive ? 'active' : ''}`}
        >
          <div className="inspira-sidebar-content">
            <div className="inspira-sidebar-button">
              <div className="inspira-button-content">
                <div className="inspira-format-info">
                  <span className="inspira-format-name">Loading formats...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isMobile && (
          <div 
            className={`inspira-sidebar-backdrop ${isSidebarActive ? 'active' : ''}`} 
            onClick={() => onFormatSelect(selectedFormat)} 
          />
        )}
      </>
    );
  }

  return (
    <>
      <div 
        ref={sidebarRef}
        className={`inspira-sidebar ${isSidebarActive ? 'active' : ''}`}
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchMove={isMobile ? onTouchMove : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
      >
        <div className="inspira-sidebar-header">
          <div className="inspira-credits-display">
            Available Credits: {userProfile ? userProfile.credits : '...'}
          </div>
        </div>
        <div className="inspira-sidebar-content">
          {Object.entries(artFormats).map(([formatName, formatData]) => (
            <button
              key={formatName}
              className={`inspira-sidebar-button ${selectedFormat === formatName ? 'selected' : ''}`}
              onClick={() => onFormatSelect(formatName)}
              title={formatData.description || formatName}
            >
              <div className="inspira-button-content">
                <img
                  src={plainImage}
                  alt={formatName}
                  loading="lazy"
                />
                <div className="inspira-format-info">
                  <span className="inspira-format-name">{formatName}</span>
                  {formatData.styleTags && formatData.styleTags.length > 0 && (
                    <div className="inspira-tags-container">
                      {formatData.styleTags.slice(0, 3).map((tag, index) => (
                        <span key={`${formatName}-tag-${index}`} className="inspira-style-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="inspira-credit-cost">
                    {pipelinesCost[formatName] || '...'} Credits
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
      {isMobile && (
        <div 
          className={`inspira-sidebar-backdrop ${isSidebarActive ? 'active' : ''}`} 
          onClick={() => onFormatSelect(selectedFormat)} 
        />
      )}
    </>
  );
};

export default Side;
